import type { AdventureBoxReward } from "../adventure/adventure_box_rewards.js";
import type {
  TournamentBoxDto,
  TournamentClaimResponseDto,
  TournamentDto,
  TournamentGrandPrizeDto,
  TournamentPayloadDto,
  TournamentPhaseDto,
  TournamentPlaceBoxItemDto,
  TournamentPlaceItemDto,
  TournamentPlaceRewardDto,
  TournamentWinReportDto,
} from "../api/dto/tournament.dto.js";
import type { WinStreakTowerBalancesDto } from "../api/dto/win_streak_tower.dto.js";
import { claimTournamentGrandPrize, claimTournamentPlace, claimTournamentProgress, getTournament } from "../api/requests/tournament.requests.js";
import { infiniteHeartsUntilMs, serverUntilMs } from "../api/player_sync.js";
import { CumulativeBoosterService } from "../cumulative_boosters/cumulative_booster_service.js";
import { PersistentCache } from "../offline/persistent_cache.js";
import { ServerClock } from "../offline/server_clock.js";
import { shouldGoLocal } from "../offline/offline_policy.js";
import { StatisticsStorage } from "../storage/statistics_storage.js";
import { PlayDialogDifficulty } from "../ui/dialog/play_dialog.js";
import { TournamentAnalytics } from "./tournament_analytics.js";
import { normalizeSectorSize } from "./tournament_progress_sectors.js";
import { logRewardCoins } from "../analytics/feature_events.js";

export interface TournamentProgressReward extends AdventureBoxReward
{
  crowns: number;
  index: number;
  claimed: boolean;
  reached: boolean;
}

export type TournamentBox = TournamentBoxDto;

export interface TournamentStanding
{
  rank: number;
  name: string;
  avatar_id: number;
  crowns: number;
  box: TournamentBox | null;
  is_player: boolean;
}

export type TournamentPhase = TournamentPhaseDto;

export type TournamentPlaceItem = TournamentPlaceItemDto;
export type TournamentPlaceBoxItem = TournamentPlaceBoxItemDto;

export const MAX_PLACE_ITEMS = 3;

export function isPlaceBoxItem( item: TournamentPlaceItem ): item is TournamentPlaceBoxItem
{
  return item.type === "box";
}

type PlacePrize = Pick<TournamentPlaceRewardDto, "coins" | "box" | "rewards" | "items">;

function placeItemsOf( prize: PlacePrize ): TournamentPlaceItem[]
{
  if ( prize.items?.length ) return prize.items.slice( 0, MAX_PLACE_ITEMS );
  const items: TournamentPlaceItem[] = [];
  const coins = prize.coins
    ?? prize.rewards.filter( reward => reward.type === "coin" ).reduce( ( sum, reward ) => sum + reward.amount, 0 );
  if ( coins > 0 ) items.push( { type: "coin", amount: coins } );
  if ( prize.box ) items.push( { type: "box", box: prize.box, rewards: prize.rewards } );
  else if ( prize.coins !== undefined ) items.push( ...prize.rewards );
  return items.slice( 0, MAX_PLACE_ITEMS );
}

function placeRewardsOf( prize: PlacePrize ): AdventureBoxReward[]
{
  const granted = prize.items?.length
    ? prize.items.slice( 0, MAX_PLACE_ITEMS ).flatMap( item => isPlaceBoxItem( item ) ? item.rewards : [ item ] )
    : [ ...( prize.coins ? [ { type: "coin" as const, amount: prize.coins } ] : [] ), ...prize.rewards ];
  const totals = new Map<AdventureBoxReward["type"], number>();
  for ( const reward of granted )
    totals.set( reward.type, ( totals.get( reward.type ) ?? 0 ) + reward.amount );
  return [ ...totals ].map( ( [ type, amount ] ) => ( { type, amount } ) );
}

const PERSIST_KEY = "tournament_v1";
const CACHE_TTL_MS = 30_000;
const DEBUG_CROWNS_KEY = "tournament_debug_bonus_crowns";

interface PersistedTournament
{
  payload: TournamentPayloadDto;
  fetched_at: number;
}

/**
 * Server-authoritative tournament state. The server picks the tournament a
 * player sees, keeps the group, crowns and claims; the client caches the last
 * payload, derives the phase from the server clock so timers roll over without
 * a refetch, and adds crowns optimistically until level-complete confirms them.
 */
class TournamentServiceImpl
{
  private payload: TournamentPayloadDto | null = null;
  private fetched_at = 0;
  private pending: Promise<TournamentPayloadDto | null> | null = null;
  private optimistic_crowns = 0;
  private listeners = new Set<() => void>();

  constructor()
  {
    const stored = PersistentCache.read<PersistedTournament>( PERSIST_KEY );
    if ( stored?.payload && typeof stored.payload === "object" )
      this.payload = stored.payload;
  }

  public onChange( listener: () => void ): () => void
  {
    this.listeners.add( listener );
    return () => this.listeners.delete( listener );
  }

  public fetchedAgoMs(): number
  {
    return this.fetched_at > 0 ? Date.now() - this.fetched_at : Number.POSITIVE_INFINITY;
  }

  public fetch( force = false ): Promise<TournamentPayloadDto | null>
  {
    if ( !force && this.payload && this.fetchedAgoMs() < CACHE_TTL_MS )
      return Promise.resolve( this.payload );
    if ( shouldGoLocal() )
      return Promise.resolve( this.payload );
    if ( this.pending ) return this.pending;

    this.pending = getTournament()
      .then( payload =>
      {
        this.adopt( payload );
        return payload;
      } )
      .catch( e =>
      {
        console.warn( "Tournament fetch failed:", e );
        return this.payload;
      } )
      .finally( () => { this.pending = null; } );
    return this.pending;
  }

  /** Refetches when the phase derived from the clock has moved past the cached one. */
  public refreshIfStale(): void
  {
    const tournament = this.payload?.tournament;
    if ( !tournament ) return;
    if ( this.phaseOf( tournament ) !== tournament.phase && this.fetchedAgoMs() > 5_000 )
      void this.fetch( true );
  }

  public current(): TournamentDto | null
  {
    const tournament = this.payload?.enabled ? this.payload.tournament : null;
    if ( !tournament ) return null;
    return this.phaseOf( tournament ) === null ? null : tournament;
  }

  public phase(): TournamentPhase | null
  {
    const tournament = this.current();
    return tournament ? this.phaseOf( tournament ) : null;
  }

  public isVisible(): boolean
  {
    const phase = this.phase();
    if ( phase === "claim" ) return Boolean( this.current()?.joined );
    return phase !== null;
  }

  public isActive(): boolean
  {
    return this.phase() === "active";
  }

  public isFinished(): boolean
  {
    return this.phase() === "claim";
  }

  public getMsUntilStart(): number
  {
    return this.msUntil( this.current()?.starts_at );
  }

  public getMsUntilEnd(): number
  {
    return this.msUntil( this.current()?.ends_at );
  }

  public getMsUntilClaimEnd(): number
  {
    return this.msUntil( this.current()?.claim_until );
  }

  public getCrowns(): number
  {
    return ( this.current()?.crowns ?? 0 ) + this.optimistic_crowns + this.debugCrowns();
  }

  /**
   * Crowns a claim can rely on: the server's plus the optimistic ones of a win it is about to
   * confirm. Debug crowns move the bar but the server refuses claims they alone reach, so they
   * would bring the same reward overlay back on every visit to the menu.
   */
  private claimableCrowns(): number
  {
    return ( this.current()?.crowns ?? 0 ) + this.optimistic_crowns;
  }

  public crownsForWin( difficulty: PlayDialogDifficulty ): number
  {
    const rules = this.current()?.crowns_rules;
    if ( !rules ) return difficulty === PlayDialogDifficulty.SuperHard ? 3 : difficulty === PlayDialogDifficulty.Hard ? 2 : 1;
    switch ( difficulty )
    {
      case PlayDialogDifficulty.SuperHard: return rules.super_hard;
      case PlayDialogDifficulty.Hard: return rules.hard;
      default: return rules.common;
    }
  }

  public crownsForDailyChallenge(): number
  {
    const rules = this.current()?.crowns_rules;
    return rules?.daily_challenge ? rules.common : 0;
  }

  /** Optimistic crowns for a win the server has not confirmed yet (offline, or before level-complete returns). */
  public addCrowns( count: number ): void
  {
    if ( !this.isActive() || count <= 0 ) return;
    this.optimistic_crowns += count;
    this.emit();
  }

  public applyWinReport( report: TournamentWinReportDto | null | undefined ): void
  {
    const tournament = this.payload?.tournament;
    if ( report && tournament && tournament.instance === report.instance )
    {
      tournament.crowns = report.crowns;
      tournament.joined = true;
      if ( report.room_id ) tournament.room_id = report.room_id;
      this.recomputeReached( tournament );
      this.optimistic_crowns = 0;
      this.persist();
      TournamentAnalytics.observe( tournament );
    }
    else if ( report )
    {
      this.optimistic_crowns = 0;
    }
    void this.fetch( true ).then( () => this.emit() );
  }

  private checkpointRows(): TournamentDto["progress_rewards"]
  {
    return this.current()?.progress_rewards ?? [];
  }

  private grandPrize(): TournamentGrandPrizeDto | null
  {
    return this.current()?.grand_prize ?? null;
  }

  /** The checkpoints, one reward each; the grand prize chest is not among them. */
  public getProgressRewards(): TournamentProgressReward[]
  {
    const crowns = this.getCrowns();
    return this.checkpointRows().flatMap( row => row.rewards.slice( 0, 1 ).map( reward => ( {
      ...reward,
      crowns: row.crowns,
      index: row.index,
      claimed: row.claimed,
      reached: crowns >= row.crowns,
    } ) ) );
  }

  public hasGrandPrize(): boolean
  {
    return this.grandPrize() !== null;
  }

  public getGrandPrizeCrowns(): number
  {
    return this.grandPrize()?.crowns ?? 0;
  }

  /** What the grand prize chest holds. */
  public getGrandPrizeRewards(): AdventureBoxReward[]
  {
    return this.grandPrize()?.rewards ?? [];
  }

  public isGrandPrizeClaimed(): boolean
  {
    return Boolean( this.grandPrize()?.claimed );
  }

  /** Every checkpoint collected and the chest's crowns reached, the chest not yet collected. */
  public isGrandPrizeClaimable(): boolean
  {
    const chest = this.grandPrize();
    const phase = this.phase();
    if ( !chest || ( phase !== "active" && phase !== "claim" ) ) return false;
    const rewards = this.getProgressRewards();
    return rewards.every( reward => reward.reached && reward.claimed )
      && this.claimableCrowns() >= chest.crowns
      && !this.isGrandPrizeClaimed();
  }

  public async claimGrandPrize(): Promise<boolean>
  {
    const tournament = this.current();
    const chest = this.grandPrize();
    if ( !tournament || !chest || !this.isGrandPrizeClaimable() || shouldGoLocal() ) return false;
    try
    {
      const response = await claimTournamentGrandPrize( tournament.instance );
      this.applyClaim( response );
      logRewardCoins( response.rewards, "tournament_grand_prize" );
      return true;
    }
    catch ( e )
    {
      console.warn( "Tournament grand prize claim failed:", e );
      void this.fetch( true );
      return false;
    }
  }

  public getProgressRewardBundle( index: number ): AdventureBoxReward[]
  {
    return this.current()?.progress_rewards.find( row => row.index === index )?.rewards ?? [];
  }

  /** Not collected yet and reached by crowns the server knows about. */
  public isProgressClaimable( reward: TournamentProgressReward ): boolean
  {
    return !reward.claimed && this.claimableCrowns() >= reward.crowns;
  }

  /** The first milestone reached by crowns the server knows about and not collected. */
  public nextClaimableProgress(): TournamentProgressReward | null
  {
    const crowns = this.claimableCrowns();
    return this.getProgressRewards().find( reward => !reward.claimed && crowns >= reward.crowns ) ?? null;
  }

  public getProgressSectorSize(): number
  {
    return normalizeSectorSize( this.current()?.progress_sector_size );
  }

  public getRewardsCollected(): number
  {
    return this.checkpointRows().filter( row => row.claimed ).length;
  }

  public getRewardsTotal(): number
  {
    return this.checkpointRows().length;
  }

  public getStandings(): TournamentStanding[]
  {
    const tournament = this.current();
    if ( !tournament ) return [];
    const extra = this.optimistic_crowns + this.debugCrowns();
    if ( extra === 0 ) return tournament.standings;
    const rows = tournament.standings.map( row => row.is_player ? { ...row, crowns: row.crowns + extra } : row );
    rows.sort( ( a, b ) => b.crowns - a.crowns || ( a.is_player ? -1 : b.is_player ? 1 : a.rank - b.rank ) );
    return rows.map( ( row, index ) => ( { ...row, rank: index + 1, box: this.placeBox( index + 1 ) } ) );
  }

  public getPlaceBox( rank: number ): TournamentBox | null
  {
    return this.placeBox( rank );
  }

  public getPlaceRewardsFor( rank: number ): AdventureBoxReward[]
  {
    const row = this.placeRow( rank );
    return row ? placeRewardsOf( row ) : [];
  }

  public getPlaceItemsFor( rank: number ): TournamentPlaceItem[]
  {
    const row = this.placeRow( rank );
    return row ? placeItemsOf( row ) : [];
  }

  public getFinishedPlace(): number
  {
    const tournament = this.current();
    return tournament?.place_reward?.rank ?? tournament?.rank ?? 0;
  }

  public getPlaceRewards(): AdventureBoxReward[]
  {
    const place_reward = this.current()?.place_reward;
    return place_reward ? placeRewardsOf( place_reward ) : [];
  }

  public hasClaimablePlaceReward(): boolean
  {
    return this.isFinished() && Boolean( this.current()?.place_reward?.claimable );
  }

  public async claimProgress( index: number ): Promise<boolean>
  {
    const tournament = this.current();
    if ( !tournament || shouldGoLocal() ) return false;
    try
    {
      const response = await claimTournamentProgress( tournament.instance, index );
      this.applyClaim( response );
      logRewardCoins( response.rewards, "tournament_step" );
      return true;
    }
    catch ( e )
    {
      console.warn( `Tournament milestone claim failed (${index}):`, e );
      void this.fetch( true );
      return false;
    }
  }

  public async claimPlace(): Promise<boolean>
  {
    const tournament = this.current();
    if ( !tournament || shouldGoLocal() ) return false;
    try
    {
      const response = await claimTournamentPlace( tournament.instance );
      this.applyClaim( response );
      logRewardCoins( response.rewards, "tournament_place" );
      return true;
    }
    catch ( e )
    {
      console.warn( "Tournament place claim failed:", e );
      void this.fetch( true );
      return false;
    }
  }

  /** Client-only crowns on top of the server's, for testing the path and standings; claims still go by server crowns. */
  public addCrownsForDebug( count: number ): void
  {
    const bonus = Math.max( -( this.current()?.crowns ?? 0 ), this.debugCrowns() + Math.round( count ) );
    try
    {
      if ( bonus === 0 ) localStorage.removeItem( DEBUG_CROWNS_KEY );
      else localStorage.setItem( DEBUG_CROWNS_KEY, String( bonus ) );
    }
    catch { /* storage unavailable: nothing to override */ }
    this.emit();
  }

  public resetCrownsForDebug(): void
  {
    this.addCrownsForDebug( -this.debugCrowns() );
  }

  private debugCrowns(): number
  {
    try
    {
      const bonus = Number.parseInt( localStorage.getItem( DEBUG_CROWNS_KEY ) ?? "", 10 );
      return Number.isFinite( bonus ) ? bonus : 0;
    }
    catch
    {
      return 0;
    }
  }

  public debugSummary(): Record<string, unknown>
  {
    const tournament = this.current();
    return {
      enabled: this.payload?.enabled ?? null,
      id: tournament?.id ?? null,
      instance: tournament?.instance ?? null,
      phase: this.phase(),
      crowns: this.getCrowns(),
      debug_crowns: this.debugCrowns(),
      rank: tournament?.rank ?? null,
      fetched_ago_ms: this.fetchedAgoMs(),
    };
  }

  private applyClaim( response: TournamentClaimResponseDto ): void
  {
    this.applyBalances( response.balances );
    if ( this.payload ) this.payload.tournament = response.tournament;
    this.persist();
    this.emit();
  }

  private applyBalances( balances: WinStreakTowerBalancesDto ): void
  {
    const stats = StatisticsStorage.load();
    StatisticsStorage.update( {
      coins: balances.coins,
      hearts: Math.max( stats.hearts, balances.hearts ),
      infinite_hearts_until: Math.max( stats.infinite_hearts_until, infiniteHeartsUntilMs( balances.infinite_hearts_until ) ),
      no_ads_until: Math.max( stats.no_ads_until, serverUntilMs( balances.no_ads_until ) ),
      fireworks: balances.boosters.fireworks,
      multiballs: balances.boosters.multi_balls,
      big_balls: balances.boosters.big_balls,
      infinite_balls: balances.boosters.infinite_balls,
    } );
    CumulativeBoosterService.applyInventory( balances.boosters );
  }

  private adopt( payload: TournamentPayloadDto ): void
  {
    const previous = this.payload?.tournament?.instance;
    this.payload = payload;
    this.fetched_at = Date.now();
    if ( payload.tournament?.instance !== previous ) this.optimistic_crowns = 0;
    this.persist();
    if ( payload.enabled ) TournamentAnalytics.observe( payload.tournament );
    this.emit();
  }

  private recomputeReached( tournament: TournamentDto ): void
  {
    for ( const row of tournament.progress_rewards )
      row.reached = tournament.crowns >= row.crowns;
    if ( tournament.grand_prize )
      tournament.grand_prize.reached = tournament.crowns >= tournament.grand_prize.crowns;
  }

  private placeRow( rank: number ): TournamentPlaceRewardDto | null
  {
    return this.current()?.place_rewards.find( row => rank >= row.from && rank <= row.to ) ?? null;
  }

  private placeBox( rank: number ): TournamentBox | null
  {
    const row = this.placeRow( rank );
    return row ? placeItemsOf( row ).find( isPlaceBoxItem )?.box ?? null : null;
  }

  private phaseOf( tournament: TournamentDto ): TournamentPhase | null
  {
    const now = ServerClock.nowMs();
    if ( now < Date.parse( tournament.announce_at ) || now >= Date.parse( tournament.claim_until ) ) return null;
    if ( now < Date.parse( tournament.starts_at ) ) return "announce";
    if ( now < Date.parse( tournament.ends_at ) ) return "active";
    return "claim";
  }

  private msUntil( iso: string | undefined ): number
  {
    if ( !iso ) return 0;
    return Math.max( 0, Date.parse( iso ) - ServerClock.nowMs() );
  }

  private persist(): void
  {
    if ( !this.payload ) return;
    PersistentCache.write( PERSIST_KEY, { payload: this.payload, fetched_at: this.fetched_at } satisfies PersistedTournament );
  }

  private emit(): void
  {
    for ( const listener of this.listeners ) listener();
  }
}

export const TournamentService = new TournamentServiceImpl();
